/**
 * Unique inventory-box identity in scans: hub URL `/i/{32hex}`.
 * Location tags are hub `/inv/{sku}/{location}` (and leftover inv.sgsops.com URLs).
 * Bare 32-hex and hub `/a/{32hex}` are arrived-package tokens, not a box.
 */

const HEX32_PATTERN = /^[0-9a-f]{32}$/iu;
const HEX8_PATTERN = /^[0-9a-f]{8}$/iu;
const BOX_PATH_PATTERN =
  /(?:^|\/)i\/([0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12})(?:[/?#]|$)/iu;
const INVENTORY_LOCATION_URL_PATTERN = /\/inv\/([^/?#]+)\/(\d+)/iu;

export function compactUuidHex(raw: string): string {
  return raw.replace(/[\s-]/gu, "").toLowerCase();
}

export function inventoryBoxHexFromId(id: string): string {
  const hex = compactUuidHex(id);
  if (!HEX32_PATTERN.test(hex)) {
    throw new Error("inventory box id must be a UUID.");
  }
  return hex;
}

export function inventoryBoxIdFromHex(hex: string): string {
  const compact = compactUuidHex(hex);
  if (!HEX32_PATTERN.test(compact)) {
    throw new Error("inventory box hex must be 32 hexadecimal characters.");
  }
  return `${compact.slice(0, 8)}-${compact.slice(8, 12)}-${compact.slice(12, 16)}-${compact.slice(16, 20)}-${compact.slice(20, 32)}`;
}

/** First eight hex characters, grouped for the carton face: C651-DD94. */
export function inventoryBoxShortCodeFromHex(hex: string): string {
  const compact = compactUuidHex(hex);
  if (compact.length < 8) {
    throw new Error("inventory box hex is too short for a carton code.");
  }
  return `${compact.slice(0, 4).toUpperCase()}-${compact.slice(4, 8).toUpperCase()}`;
}

export function inventoryBoxShortCodeFromScanUrl(url: string): string | null {
  const hex = parseInventoryBoxIdFromScan(url);
  if (hex == null) return null;
  return inventoryBoxShortCodeFromHex(hex);
}

/** Printed carton code or eight hex characters. Not a unique id by itself. */
export function parseInventoryBoxShortPrefix(raw: string): string | null {
  const compact = compactUuidHex(raw.trim());
  if (!HEX8_PATTERN.test(compact)) return null;
  return compact;
}

/** 32-hex id from a hub inventory-box URL or path. Does not treat bare hex as a box. */
export function parseInventoryBoxIdFromScan(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const match = trimmed.match(BOX_PATH_PATTERN);
  const captured = match?.[1];
  if (captured == null) return null;
  const hex = compactUuidHex(captured);
  return HEX32_PATTERN.test(hex) ? hex : null;
}

export type InventoryLocationScan = {
  sku: string;
  locationId: number;
};

export type LegacyInventoryAdjustmentScan = InventoryLocationScan;

/**
 * Location-tag URL: hub `/inv/{sku}/{location}` or leftover inv.sgsops.com labels.
 * Unique carton URLs are `/i/{32hex}` and are not this.
 */
export function parseInventoryLocationUrl(raw: string): InventoryLocationScan | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const match = trimmed.match(INVENTORY_LOCATION_URL_PATTERN);
  if (match?.[1] == null || match[2] == null) return null;
  let sku = match[1];
  try {
    sku = decodeURIComponent(sku);
  } catch {
    // Keep the raw path segment when it is not valid percent-encoding.
  }
  const locationId = Number.parseInt(match[2], 10);
  if (sku.trim() === "" || !Number.isFinite(locationId) || locationId <= 0) return null;
  return { sku: sku.trim(), locationId };
}

export function parseLegacyInventoryAdjustmentUrl(raw: string): InventoryLocationScan | null {
  return parseInventoryLocationUrl(raw);
}

/** Route param or pasted URL → 32-hex, or null. */
export function normalizeInventoryBoxHex(raw: string): string | null {
  const compact = compactUuidHex(raw.trim());
  if (HEX32_PATTERN.test(compact)) return compact;
  return parseInventoryBoxIdFromScan(raw);
}
